import { useRef, useState, useCallback, useEffect } from "react";
import { log } from "utils/logger";

/**
 * Throttle leading+trailing sobre una función de refresh.
 *
 * - shouldRefresh() false → solo incrementa pendingCount (badge).
 * - shouldRefresh() true → refresh inmediato (leading) + cooldown.
 * - Eventos durante cooldown → un refresh más al terminar (trailing).
 * - Si la condición cambió al terminar el cooldown → suma a pendingCount.
 */
export function useThrottledRefresh({ ms = 5000, shouldRefresh, onRefresh }) {
    const [pendingCount, setPendingCount] = useState(0);
    const timeoutRef = useRef(null);
    const pendingRefreshRef = useRef(false);

    const shouldRefreshRef = useRef(shouldRefresh);
    const onRefreshRef = useRef(onRefresh);
    shouldRefreshRef.current = shouldRefresh;
    onRefreshRef.current = onRefresh;

    const trigger = useCallback(() => {
        if (!shouldRefreshRef.current()) {
            setPendingCount((prev) => prev + 1);
            return;
        }

        if (timeoutRef.current) {
            pendingRefreshRef.current = true;
            return;
        }

        log.debug("⚡ Refresh inmediato (leading)");
        onRefreshRef.current?.();

        timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;

            if (pendingRefreshRef.current) {
                pendingRefreshRef.current = false;
                if (shouldRefreshRef.current()) {
                    log.debug("🔁 Refresh rezagado (trailing)");
                    onRefreshRef.current?.();
                } else {
                    setPendingCount((prev) => prev + 1);
                }
            }
        }, ms);
    }, [ms]);

    const resetPendingCount = useCallback(() => {
        setPendingCount(0);
    }, []);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    return { trigger, pendingCount, resetPendingCount };
}
